// ─────────────────────────────────────────────────────────────────────────────
// Microplastic-relevant cosmetic / personal-care ingredient database.
//
// Single source of truth used by:
//   - /microplastic-ingredient-checker (paste-to-flag tool)
//   - /microplastic-ingredients (index page)
//   - /microplastic-ingredients/[slug] (one page per ingredient)
//
// Each entry is a self-contained SEO unit: the slug is the URL, the queryHook
// is the target search ("is polyethylene a microplastic"), and the longer
// fields populate the per-ingredient page so each ranks for the ingredient-
// specific intent that today bounces around the homepage and the catch-all
// ingredient checker.
//
// SEO leverage: 32 ingredients × 3-5 target queries each = ~100 long-tail
// queries we capture per-page instead of routing everything to a single
// generic checker page. This is the highest-leverage programmatic SEO play
// the site has — every query in the family has its canonical answer page.
// ─────────────────────────────────────────────────────────────────────────────

import { neighbours } from '@/utils/relatedNeighbours'

// 'none' exists only for the CLEARED_INGREDIENTS set below. Nothing with
// severity 'none' is ever passed to the ingredient checker, so the checker's
// severity-keyed style/count maps never receive it.
export type IngredientSeverity = 'high' | 'medium' | 'low' | 'none'
export type IngredientCategory =
  | 'Microplastic polymer'
  | 'Silicone'
  | 'PEG / glycol'
  | 'Acrylate / vinyl polymer'
  | 'Plasticiser / chemical of concern'
  | 'Cleared / not a microplastic'

export interface IngredientFlag {
  /** URL-safe identifier used as /microplastic-ingredients/[slug]. */
  slug: string
  /** Canonical display name (matches what appears on a cosmetics label). */
  name: string
  /** Lower-case substrings the IngredientChecker scanner matches against. */
  terms: string[]
  category: IngredientCategory
  severity: IngredientSeverity
  /** ≤ 280 chars. Top-of-card explainer used by the scanner UI + per-page hero. */
  explainer: string
  /** Where the ingredient typically appears (4-8 short examples). */
  useCases?: string[]
  /** Regulatory status, EU / US side-by-side. */
  regulation?: {
    eu: string
    us: string
  }
  /** 3-5 alternative ingredients or pick directions for cleaner formulations. */
  alternatives?: string[]
  /** Specific SERP query this page is built to capture. */
  queryHook?: string
}

// Note on ordering: longer / more specific terms first inside `terms` arrays
// so the scanner matcher prefers "acrylates copolymer" over a bare "acrylates".
// Entries themselves are grouped by category for readability.
/**
 * Date this dataset last changed; sitemap lastmod reads this, not the build time.
 * See the note on PACKAGING_DATA_DATE for why.
 */
export const INGREDIENTS_DATA_DATE = '2026-07-30'

export const INGREDIENTS: IngredientFlag[] = [
  // ── Hard plastic polymers (high severity) ─────────────────────────────────
  {
    slug: 'polyethylene',
    name: 'Polyethylene',
    terms: ['polyethylene'],
    category: 'Microplastic polymer',
    severity: 'high',
    explainer:
      'Solid plastic polymer. The EU restricted intentionally added microplastics in rinse-off cosmetics from October 2023, including polyethylene microbeads.',
    useCases: [
      'Exfoliating face and body scrubs (as microbeads, now restricted in EU)',
      'Toothpaste (banned in EU since 2018; still in some US formulas)',
      'Sunscreen (as opacifier or texture modifier)',
      'Mascara and eyeliner (as film-former)',
      'Soaps and body washes (legacy formulations)',
    ],
    regulation: {
      eu: 'Restricted in rinse-off cosmetics since October 2023 under EU regulation 2023/2055 on intentionally added microplastics.',
      us: 'Microbead-Free Waters Act 2015 banned plastic microbeads in rinse-off products. Other polyethylene uses remain unrestricted.',
    },
    alternatives: [
      'Apricot kernel powder, walnut shell, or rice powder for physical exfoliation',
      'Silica or aluminum hydroxide as opacifier',
      'Beeswax or carnauba wax as natural film-former',
    ],
    queryHook: 'is polyethylene a microplastic',
  },
  {
    slug: 'polypropylene',
    name: 'Polypropylene',
    terms: ['polypropylene'],
    category: 'Microplastic polymer',
    severity: 'high',
    explainer: 'Solid plastic polymer, often used in scrubs, mascara fibers, and exfoliants.',
    useCases: [
      'Mascara fibers and lash primers',
      'Exfoliating scrubs (legacy formulations)',
      'Pressed-powder makeup (as binder)',
      'Plastic packaging that contacts the formula',
    ],
    regulation: {
      eu: 'Restricted in rinse-off cosmetics under EU 2023/2055.',
      us: 'No specific cosmetics restriction; only packaging-related guidance.',
    },
    alternatives: ['Bamboo or rayon fibers in mascara', 'Plant-derived starches as binder'],
    queryHook: 'polypropylene in cosmetics',
  },
  {
    slug: 'polyethylene-terephthalate',
    name: 'Polyethylene Terephthalate (PET)',
    terms: ['polyethylene terephthalate', 'pet polymer'],
    category: 'Microplastic polymer',
    severity: 'high',
    explainer: 'The same plastic used in bottles, here as glitter or shimmer in cosmetics.',
    useCases: ['Cosmetic glitter', 'Shimmer eyeshadow and highlighter', 'Nail polish'],
    regulation: {
      eu: 'Glitter and shimmer applications restricted under EU 2023/2055 with phased compliance through 2027.',
      us: 'No federal restriction; some state-level glitter bans (California 2024).',
    },
    alternatives: ['Bio-glitter from cellulose', 'Mica + iron-oxide pigments for shimmer'],
    queryHook: 'is PET in cosmetics safe',
  },
  {
    slug: 'pmma',
    name: 'Polymethyl Methacrylate (PMMA)',
    terms: ['polymethyl methacrylate', 'pmma'],
    category: 'Microplastic polymer',
    severity: 'high',
    explainer: 'Solid acrylic plastic used as a microbead filler. Banned in EU rinse-off products since 2023.',
    useCases: ['Filler in foundations and primers', 'Lip plumpers', 'Anti-aging fillers (cosmetic-grade)'],
    regulation: {
      eu: 'Restricted in rinse-off products since October 2023 under EU 2023/2055.',
      us: 'No federal cosmetic restriction; medical-grade PMMA is FDA-cleared for specific dermal fillers.',
    },
    alternatives: ['Silica microspheres', 'Cellulose microspheres', 'Tapioca starch as soft-focus filler'],
    queryHook: 'is PMMA a microplastic',
  },
  {
    slug: 'nylon',
    name: 'Nylon-12 / Nylon-6',
    // Narrowed 2026-07-30. Batch A added dedicated nylon-12 / nylon-6 / nylon-66
    // pages, so this legacy entry must not also claim those tokens or two pages
    // compete for the same ingredient. It stays as the bare-family page.
    terms: ['nylon', 'polyamide-12', 'polyamide 12'],
    category: 'Microplastic polymer',
    severity: 'high',
    explainer: 'Plastic powder used in mattifying primers, mascara, and loose powders.',
    useCases: ['Mattifying primers', 'Loose setting powders', 'Mascara (as fiber extender)', 'Anti-aging serums (as soft-focus optical filler)'],
    regulation: {
      eu: 'Restricted in rinse-off cosmetics; leave-on restrictions phased through 2027.',
      us: 'No federal restriction.',
    },
    alternatives: ['Silica', 'Rice powder', 'Cellulose powder'],
    queryHook: 'is nylon-12 a microplastic',
  },
  {
    slug: 'polyurethane',
    name: 'Polyurethane',
    terms: ['polyurethane'],
    category: 'Microplastic polymer',
    severity: 'high',
    explainer: 'Plastic polymer family, used as a film-former in long-wear makeup and hair products.',
    useCases: ['Long-wear foundations and concealers', 'Mascara (waterproof formulas)', 'Hair gel and styling cream', 'Nail polish'],
    regulation: {
      eu: 'Leave-on polyurethane is restricted with phased compliance through 2027 under EU 2023/2055.',
      us: 'No federal restriction.',
    },
    alternatives: ['Plant-derived shellac', 'Beeswax as film-former'],
    queryHook: 'polyurethane in skincare',
  },
  {
    slug: 'polystyrene',
    name: 'Polystyrene',
    terms: ['polystyrene'],
    category: 'Microplastic polymer',
    severity: 'high',
    explainer: 'Plastic polymer occasionally used as opacifier or microbead.',
    useCases: ['Opacifier in lotions', 'Filler in pressed powders', 'Rare ingredient, most common in packaging'],
    regulation: {
      eu: 'Restricted under EU 2023/2055.',
      us: 'No federal cosmetic restriction.',
    },
    alternatives: ['Silica', 'Titanium dioxide as opacifier'],
    queryHook: 'is polystyrene in cosmetics',
  },

  // ── Acrylate / vinyl polymer family ───────────────────────────────────────
  {
    slug: 'acrylates-copolymer',
    name: 'Acrylates Copolymer',
    terms: ['acrylates copolymer', 'acrylates/acrylamide copolymer'],
    category: 'Acrylate / vinyl polymer',
    severity: 'high',
    explainer: 'Synthetic film-forming plastic used widely in makeup, sunscreens, and hair fixatives.',
    useCases: ['Sunscreen (water-resistance booster)', 'Long-wear foundation', 'Hair spray and styling gel', 'Brow gel'],
    regulation: {
      eu: 'Leave-on synthetic polymers restricted with phased compliance through 2027.',
      us: 'No federal restriction.',
    },
    alternatives: ['Plant-derived xanthan gum', 'Cassia gum'],
    queryHook: 'is acrylates copolymer a microplastic',
  },
  {
    slug: 'acrylates-crosspolymer',
    name: 'Acrylates Crosspolymer',
    // Narrowed 2026-07-31. Batch A added a dedicated
    // acrylates-c10-30-alkyl-acrylate-crosspolymer entry at severity 'low' —
    // correctly, since that one is a swellable gel network rather than a solid
    // bead. This generic entry is 'high', so while it also claimed that exact
    // INCI name it won the match and made a gel thickener read as a solid
    // microplastic. That contradicted our own published page for it, and would
    // have failed products in assessment on a technicality.
    terms: ['acrylates crosspolymer'],
    category: 'Acrylate / vinyl polymer',
    severity: 'high',
    explainer: 'Cross-linked acrylic polymer thickener. Same plastic family as Acrylates Copolymer.',
    useCases: ['Gel-cream moisturizers', 'Serums (as thickener)', 'Sunscreen lotions'],
    regulation: { eu: 'Restricted with phased compliance', us: 'No restriction' },
    alternatives: ['Xanthan gum', 'Sclerotium gum'],
    queryHook: 'acrylates crosspolymer side effects',
  },
  {
    slug: 'carbomer',
    name: 'Carbomer',
    terms: ['carbomer'],
    category: 'Acrylate / vinyl polymer',
    severity: 'medium',
    explainer:
      'Cross-linked polyacrylic acid thickener. Synthetic polymer, though much smaller particles than microbeads.',
    useCases: ['Most water-based gels and serums', 'Aloe vera gel formulations', 'Sunscreen lotions', 'Toothpaste gel'],
    regulation: { eu: 'Permitted; consultation ongoing', us: 'No restriction' },
    alternatives: ['Xanthan gum', 'Carrageenan'],
    queryHook: 'is carbomer a microplastic',
  },
  {
    slug: 'polyacrylate',
    name: 'Polyacrylate',
    terms: ['polyacrylate', 'sodium polyacrylate'],
    category: 'Acrylate / vinyl polymer',
    severity: 'medium',
    explainer: 'Polymer thickener / absorbent. Plastic family used in absorbent gels and lotions.',
  },
  {
    slug: 'methacrylate-copolymers',
    name: 'Methacrylate Copolymers',
    terms: ['methacrylate copolymer', 'ethyl methacrylate', 'methyl methacrylate'],
    category: 'Acrylate / vinyl polymer',
    severity: 'high',
    explainer: 'Acrylic plastic family used in nail products and long-wear formulas.',
  },
  {
    slug: 'pvp',
    name: 'PVP (Polyvinylpyrrolidone)',
    terms: ['polyvinylpyrrolidone', 'pvp '],
    category: 'Acrylate / vinyl polymer',
    severity: 'medium',
    explainer: 'Synthetic polymer film-former, commonly in hairspray and brow gels.',
  },
  {
    slug: 'vp-va-copolymer',
    name: 'VP/VA Copolymer',
    terms: ['vp/va copolymer', 'vp/vb copolymer'],
    category: 'Acrylate / vinyl polymer',
    severity: 'medium',
    explainer: 'Vinyl-pyrrolidone copolymer film-former, same family as PVP.',
  },
  {
    slug: 'polyquaternium',
    name: 'Polyquaternium',
    terms: ['polyquaternium'],
    category: 'Acrylate / vinyl polymer',
    severity: 'medium',
    explainer: 'Synthetic cationic polymer family used as a hair conditioner.',
  },

  // ── Silicones (siloxanes) ─────────────────────────────────────────────────
  {
    slug: 'dimethicone',
    name: 'Dimethicone',
    terms: ['dimethicone'],
    category: 'Silicone',
    severity: 'medium',
    explainer:
      'Silicone polymer (polydimethylsiloxane). Not the same as solid microplastic but classed as a synthetic polymer; EU is consulting on tighter limits.',
    useCases: ['Hair conditioner and serum (for shine)', 'Primer and foundation (for slip)', 'Moisturizers', 'Anti-aging creams'],
    regulation: {
      eu: 'D4 and D5 cyclic siloxanes restricted; linear dimethicone (PDMS) under consultation for tighter limits.',
      us: 'No federal restriction; FDA approves for cosmetic and OTC use.',
    },
    alternatives: ['Squalane (plant-derived)', 'Jojoba oil', 'Argan oil for slip'],
    queryHook: 'is dimethicone a microplastic',
  },
  {
    slug: 'cyclomethicone',
    name: 'Cyclomethicone / Cyclopentasiloxane',
    terms: ['cyclomethicone', 'cyclopentasiloxane', 'cyclohexasiloxane', 'cyclotetrasiloxane'],
    category: 'Silicone',
    severity: 'medium',
    explainer: 'Cyclic siloxanes. D4 and D5 variants are EU-restricted in rinse-off products.',
  },
  {
    slug: 'trimethicone',
    name: 'Trimethicone / Phenyl Trimethicone',
    terms: ['phenyl trimethicone', 'trimethicone'],
    category: 'Silicone',
    severity: 'low',
    explainer: 'Silicone polymer variant used for slip and shine.',
  },
  {
    slug: 'polysilicone',
    name: 'Polysilicone-X',
    terms: ['polysilicone'],
    category: 'Silicone',
    severity: 'medium',
    explainer: 'Synthetic silicone polymer family used as film-formers in long-wear makeup.',
  },

  // ── PEG / glycol family ───────────────────────────────────────────────────
  {
    slug: 'peg',
    name: 'PEG (Polyethylene Glycol)',
    terms: ['polyethylene glycol', 'peg-'],
    category: 'PEG / glycol',
    severity: 'medium',
    explainer:
      'Synthetic polymer / surfactant family. Made with ethylene oxide, so it can carry trace 1,4-dioxane, a manufacturing by-product; FDA\'s 2008 survey found none in 80% of the products tested.',
    useCases: ['Moisturizers', 'Sunscreens (as emulsifier)', 'Shampoos and conditioners', 'Toothpaste (as thickener)'],
    regulation: {
      eu: 'Permitted but ethylene oxide impurities and 1,4-dioxane contamination flagged in ongoing safety review.',
      us: 'FDA permits cosmetic use; California Prop 65 requires 1,4-dioxane labeling above threshold.',
    },
    alternatives: ['Glycerin', 'Sorbitol', 'Plant-derived emulsifying wax NF'],
    queryHook: 'is PEG a microplastic',
  },
  {
    slug: 'ppg',
    name: 'PPG (Polypropylene Glycol)',
    terms: ['polypropylene glycol', 'ppg-'],
    category: 'PEG / glycol',
    severity: 'medium',
    explainer: 'Synthetic polymer in the propylene glycol family.',
  },

  // ── Chemicals of concern that travel with plastics ────────────────────────
  {
    slug: 'parabens',
    name: 'Parabens',
    terms: ['methylparaben', 'propylparaben', 'butylparaben', 'ethylparaben', 'paraben'],
    category: 'Plasticiser / chemical of concern',
    severity: 'medium',
    explainer:
      'Preservatives flagged for endocrine activity; commonly co-occur with plastic polymers in synthetic formulas.',
    useCases: ['Moisturizers and lotions', 'Shampoo and body wash', 'Makeup'],
    regulation: {
      eu: 'Butyl and propylparaben restricted in leave-on products for infants since 2014.',
      us: 'FDA permits; California Prop 65 lists for cosmetic exposure.',
    },
    alternatives: ['Phenoxyethanol', 'Sodium benzoate + potassium sorbate', 'Sorbic acid'],
    queryHook: 'are parabens microplastics',
  },
  {
    slug: 'phthalates',
    name: 'Phthalates',
    terms: ['phthalate', 'dehp', 'dbp'],
    category: 'Plasticiser / chemical of concern',
    severity: 'high',
    explainer:
      'Plasticisers used to soften plastic, banned from many products. Strong endocrine disruptors.',
    useCases: ['Nail polish (as DBP)', 'Fragrance carriers', 'Hair spray', 'Vinyl-based packaging'],
    regulation: {
      eu: 'DEHP, DBP, BBP banned in cosmetics. Other phthalates phased out.',
      us: 'DEHP banned in childrens products; FDA monitors cosmetics use but no federal cosmetics ban.',
    },
    alternatives: ['Adipates (low-phthalate substitutes)', 'Citrate esters', 'Look for "phthalate-free" labeling'],
    queryHook: 'are phthalates microplastics',
  },
  {
    slug: 'bht-bha',
    name: 'BHT / BHA',
    terms: ['butylated hydroxytoluene', 'butylated hydroxyanisole', 'bht ', 'bha '],
    category: 'Plasticiser / chemical of concern',
    severity: 'low',
    explainer: 'Synthetic antioxidants, co-occurrence flag in plastic-heavy formulas.',
  },

  // ── July 2026 expansion ───────────────────────────────────────────────────
  // Added the highest-frequency label polymers still missing from the DB.
  // Each is verified against the EU 2023/2055 synthetic-polymer-microparticle
  // restriction (REACH) and Beat the Microbead's ingredient list. These are the
  // names that show up most on real US shampoo, sunscreen, primer, and lip
  // labels — so the paste-to-flag tool stops missing them and each slug earns
  // its own "is <name> a microplastic" long-tail page.
  {
    slug: 'styrene-acrylates-copolymer',
    name: 'Styrene/Acrylates Copolymer',
    terms: ['styrene/acrylates copolymer', 'styrene acrylates copolymer', 'styrene/acrylates/ammonium methacrylate copolymer'],
    category: 'Acrylate / vinyl polymer',
    severity: 'high',
    explainer:
      'Solid plastic microparticle used as an opacifier and to give body wash and shampoo their pearly look. One of the microplastics the EU restriction was specifically written to capture in rinse-off products.',
    useCases: [
      'Body wash and shower gel (as opacifier / pearliser)',
      'Shampoo and conditioner',
      'Liquid hand soap',
      'Face wash and cleansers',
    ],
    regulation: {
      eu: 'Captured as a synthetic polymer microparticle under EU 2023/2055; opacifier use in rinse-off products restricted from October 2023.',
      us: 'No federal cosmetic restriction.',
    },
    alternatives: ['Mica for pearl effect', 'Titanium dioxide as opacifier', 'Unpearlised clear formulas'],
    queryHook: 'is styrene acrylates copolymer a microplastic',
  },
  {
    slug: 'polyacrylamide',
    name: 'Polyacrylamide',
    terms: ['polyacrylamide', 'acrylamide/sodium acryloyldimethyltaurate copolymer', 'polyacrylate crosspolymer-6'],
    category: 'Acrylate / vinyl polymer',
    severity: 'medium',
    explainer:
      'Synthetic acrylic polymer used as a thickener and film-former. The polymer itself is large, but the flag is the residual acrylamide monomer, which the IARC classes as a probable human carcinogen.',
    useCases: ['Sunscreen and lotion (as thickener)', 'Anti-aging serums (film-former)', 'Foundation and BB cream', 'Sheet-mask essences'],
    regulation: {
      eu: 'Permitted; residual acrylamide monomer capped at 0.1 ppm in leave-on and 0.5 ppm in rinse-off products.',
      us: 'No federal cosmetic restriction; California Prop 65 lists acrylamide.',
    },
    alternatives: ['Xanthan gum', 'Sclerotium gum', 'Hydroxyethylcellulose'],
    queryHook: 'is polyacrylamide a microplastic',
  },
  {
    slug: 'vp-eicosene-copolymer',
    name: 'VP/Eicosene Copolymer',
    terms: ['vp/eicosene copolymer', 'vp/hexadecene copolymer', 'tricontanyl pvp'],
    category: 'Acrylate / vinyl polymer',
    severity: 'medium',
    explainer: 'Vinylpyrrolidone copolymer film-former, the same PVP family, used to make sunscreen and mascara water- and transfer-resistant.',
    useCases: ['Sunscreen (water-resistance)', 'Waterproof mascara', 'Long-wear lipstick', 'Setting sprays'],
    regulation: { eu: 'Leave-on synthetic polymers restricted with phased compliance through 2027 under EU 2023/2055.', us: 'No federal restriction.' },
    alternatives: ['Plant-derived shellac', 'Beeswax film-formers'],
    queryHook: 'is vp eicosene copolymer a microplastic',
  },
  {
    slug: 'dimethicone-crosspolymer',
    name: 'Dimethicone Crosspolymer',
    terms: ['dimethicone crosspolymer', 'dimethicone/vinyl dimethicone crosspolymer', 'vinyl dimethicone crosspolymer'],
    category: 'Silicone',
    severity: 'medium',
    explainer:
      'Cross-linked silicone elastomer that creates the soft, blurring "slip" in primers and pore-fillers. A synthetic polymer network rather than a solid bead, but classed with the siloxanes the EU is tightening.',
    useCases: ['Face primers and pore-blurring products', 'Foundation and BB cream (for slip)', 'Oil-control mattifiers', 'Color cosmetics'],
    regulation: {
      eu: 'Cyclic siloxanes D4/D5/D6 restricted; silicone elastomers under ongoing consultation under EU 2023/2055.',
      us: 'No federal restriction; FDA permits cosmetic use.',
    },
    alternatives: ['Squalane', 'Cornstarch-based soft-focus powders', 'Silica microspheres'],
    queryHook: 'is dimethicone crosspolymer a microplastic',
  },
  {
    slug: 'amodimethicone',
    name: 'Amodimethicone',
    terms: ['amodimethicone', 'trideceth-', 'cetrimonium chloride amodimethicone'],
    category: 'Silicone',
    severity: 'low',
    explainer: 'Amino-functional silicone polymer that binds to hair to smooth and detangle. Same siloxane family as dimethicone, harder to rinse out.',
    useCases: ['Conditioner and hair masks', 'Leave-in conditioners', 'Anti-frizz serums', 'Color-protect shampoos'],
    regulation: { eu: 'Permitted; part of the siloxane group under ongoing EU review.', us: 'No federal restriction.' },
    alternatives: ['Behentrimonium methosulfate', 'Cetearyl alcohol', 'Plant oils for slip'],
    queryHook: 'is amodimethicone a silicone',
  },
  {
    slug: 'trimethylsiloxysilicate',
    name: 'Trimethylsiloxysilicate',
    terms: ['trimethylsiloxysilicate', 'polypropylsilsesquioxane', 'polymethylsilsesquioxane'],
    category: 'Silicone',
    severity: 'medium',
    explainer: 'Silicone resin film-former that makes long-wear lipstick and foundation transfer-proof. A synthetic polymer that stays on skin for hours.',
    useCases: ['Long-wear and "transfer-proof" lipstick', '16-hour foundations', 'Waterproof concealer', 'Sweat-resistant sunscreen'],
    regulation: { eu: 'Silicone resins under consultation within the EU 2023/2055 framework.', us: 'No federal restriction.' },
    alternatives: ['Beeswax and candelilla-wax films', 'Non-transfer-proof formulas'],
    queryHook: 'is trimethylsiloxysilicate a microplastic',
  },
  {
    slug: 'polybutylene-terephthalate',
    name: 'Polybutylene Terephthalate (PBT)',
    terms: ['polybutylene terephthalate', 'pbt'],
    category: 'Microplastic polymer',
    severity: 'high',
    explainer: 'Solid polyester plastic, PET’s close cousin, used with PET to make cosmetic glitter and shimmer particles.',
    useCases: ['Cosmetic glitter', 'Shimmer eyeshadow and highlighter', 'Festival and body glitter', 'Nail art'],
    regulation: {
      eu: 'Glitter and shimmer applications restricted under EU 2023/2055 with phased compliance through 2027.',
      us: 'No federal restriction; some state-level glitter bans.',
    },
    alternatives: ['Cellulose bio-glitter', 'Mica + iron-oxide shimmer'],
    queryHook: 'is polybutylene terephthalate a microplastic',
  },
  {
    slug: 'hydrogenated-polyisobutene',
    name: 'Hydrogenated Polyisobutene',
    terms: ['hydrogenated polyisobutene', 'polyisobutene', 'polybutene'],
    category: 'Microplastic polymer',
    severity: 'low',
    explainer: 'Synthetic hydrocarbon polymer used as a shine-giving emollient in lip gloss and foundations, a common petroleum-derived swap for mineral oil.',
    useCases: ['Lip gloss and liquid lipstick (for shine)', 'Foundation and primer', 'Cream blush', 'Hair-shine serums'],
    regulation: { eu: 'Permitted; liquid polymer, not a solid microparticle, so outside the microbead restriction.', us: 'No federal restriction.' },
    alternatives: ['Jojoba oil', 'Squalane', 'Castor-seed oil for gloss'],
    queryHook: 'is hydrogenated polyisobutene a microplastic',
  },

  // ── Batch A, 2026-07-30: named polymer INCI entries ───────────────────────
  // The original 32 covered generic families ("nylon", "polyurethane"). Labels
  // rarely say that — they say "Nylon-12" or "Polyurethane-35". Each specific
  // INCI name is its own search, so each gets its own page.
  {
    slug: 'nylon-12',
    name: 'Nylon-12',
    terms: ['nylon-12', 'nylon 12'],
    category: 'Microplastic polymer',
    severity: 'high',
    explainer:
      'Solid polyamide microsphere used to blur pores and absorb oil. One of the most common intentionally added microplastics in colour cosmetics, and a leave-on product so no rinse-off restriction applies.',
    useCases: ['Primers and pore-blurring products', 'Loose and pressed powders', 'Mattifying foundations', 'Eyeshadow (for slip)'],
    regulation: { eu: 'Leave-on cosmetics must be reformulated by 2035 under the EU microplastics restriction; rinse-off already restricted.', us: 'No federal restriction.' },
    alternatives: ['Rice starch', 'Tapioca starch', 'Silica (mineral)', 'Bamboo powder'],
    queryHook: 'is nylon-12 a microplastic',
  },
  {
    slug: 'nylon-6',
    name: 'Nylon-6',
    terms: ['nylon-6', 'nylon 6'],
    category: 'Microplastic polymer',
    severity: 'high',
    explainer:
      'Polyamide polymer, chemically the same family as Nylon-12 but a different chain length. Appears as fibres in mascara and as powder in complexion products.',
    useCases: ['Lengthening mascara (as fibres)', 'Pressed powder', 'Cream blush', 'Nail products'],
    regulation: { eu: 'Covered by the EU intentionally-added-microplastics restriction, with leave-on deadlines phased to 2035.', us: 'No federal restriction.' },
    alternatives: ['Rayon or cellulose fibres in mascara', 'Rice starch', 'Silica'],
    queryHook: 'is nylon-6 a microplastic',
  },
  {
    slug: 'nylon-66',
    name: 'Nylon-66',
    terms: ['nylon-66', 'nylon 66', 'nylon-6/66'],
    category: 'Microplastic polymer',
    severity: 'high',
    explainer:
      'Polyamide used where a harder particle is wanted. Same microplastic classification as other nylons, common in long-wear powders.',
    useCases: ['Long-wear powders', 'Mascara fibres', 'Body glitter and shimmer'],
    regulation: { eu: 'Within scope of the EU microplastics restriction.', us: 'No federal restriction.' },
    alternatives: ['Mica for shimmer', 'Rice starch', 'Cellulose'],
    queryHook: 'is nylon-66 a microplastic',
  },
  {
    slug: 'ptfe',
    name: 'PTFE (Polytetrafluoroethylene)',
    terms: ['ptfe', 'polytetrafluoroethylene', 'teflon'],
    category: 'Microplastic polymer',
    severity: 'high',
    explainer:
      'The Teflon polymer, and both a microplastic and a PFAS. Used for slip in cosmetics. Being a fluoropolymer, it carries the persistence questions of the whole PFAS class on top of being a solid plastic particle.',
    useCases: ['Long-wear foundation', 'Primer (for slip)', 'Pressed powder', 'Some lipsticks'],
    regulation: { eu: 'A microplastic under the restriction and within scope of the proposed universal PFAS restriction.', us: 'Some US states restrict PFAS in cosmetics; no federal ban.' },
    alternatives: ['Silica', 'Mica', 'Squalane for slip'],
    queryHook: 'is ptfe a microplastic',
  },
  {
    slug: 'polyester-1',
    name: 'Polyester-1 (and Polyester-3 to -11)',
    terms: ['polyester-1', 'polyester-3', 'polyester-5', 'polyester-11', 'polyester'],
    category: 'Microplastic polymer',
    severity: 'high',
    explainer:
      'Numbered polyester polymers used as film-formers and glitter substrates. Cosmetic glitter is very often polyester film, which is a microplastic by any definition.',
    useCases: ['Glitter and shimmer particles', 'Long-wear film-formers', 'Nail polish', 'Body shimmer'],
    regulation: { eu: 'Cosmetic glitter made from plastic film is restricted in the EU; loose plastic glitter was banned from October 2023.', us: 'No federal restriction.' },
    alternatives: ['Mica-based shimmer', 'Mineral glitter', 'Synthetic fluorphlogopite'],
    queryHook: 'is polyester in cosmetics a microplastic',
  },
  {
    slug: 'pvc',
    name: 'PVC (Polyvinyl Chloride)',
    terms: ['pvc', 'polyvinyl chloride'],
    category: 'Microplastic polymer',
    severity: 'high',
    explainer:
      'Rare in modern cosmetics but still found in some applicators, glitters and novelty packaging. A solid microplastic, and PVC production and plasticiser history make it the least desirable polymer to find anywhere near a cosmetic.',
    useCases: ['Novelty glitter', 'Some applicators and packaging components', 'Nail decals'],
    regulation: { eu: 'Within the microplastics restriction where present as particles.', us: 'No cosmetic-specific restriction.' },
    alternatives: ['Glass or aluminium packaging', 'Mineral glitter'],
    queryHook: 'is pvc a microplastic',
  },
  {
    slug: 'polyvinyl-acetate',
    name: 'Polyvinyl Acetate (PVA / PVAc)',
    terms: ['polyvinyl acetate', 'pvac', 'vinyl acetate'],
    category: 'Microplastic polymer',
    severity: 'medium',
    explainer:
      'Synthetic film-forming polymer, essentially the base of white glue. Used for hold and water resistance. Forms a plastic film on skin, hair or lashes rather than a solid particle.',
    useCases: ['Peel-off masks', 'Strong-hold hair products', 'Waterproof mascara', 'Lash adhesives'],
    regulation: { eu: 'Permitted; a film-former rather than a solid microbead, so outside the particle restriction.', us: 'No federal restriction.' },
    alternatives: ['Shellac (natural resin)', 'Clay masks instead of peel-off', 'Acacia gum'],
    queryHook: 'is polyvinyl acetate a microplastic',
  },
  {
    slug: 'polyvinyl-alcohol',
    name: 'Polyvinyl Alcohol (PVOH / PVA)',
    terms: ['polyvinyl alcohol', 'pvoh', 'pva'],
    category: 'Microplastic polymer',
    severity: 'medium',
    explainer:
      'Water-soluble synthetic polymer, the film used for laundry and dishwasher pods and for peel-off face masks. Marketed as dissolving, but dissolving is not the same as degrading, and how completely it breaks down in treatment plants is genuinely contested.',
    useCases: ['Peel-off face masks', 'Laundry and dishwasher pod film', 'Some sheet masks', 'Hair styling films'],
    regulation: { eu: 'Water-soluble polymers are currently outside the solid-microplastic restriction, which is itself a point of scientific dispute.', us: 'No federal restriction.' },
    alternatives: ['Powder or liquid detergent over pods', 'Clay masks', 'Cellulose sheet masks'],
    queryHook: 'is polyvinyl alcohol a microplastic',
  },
  {
    slug: 'polylactic-acid',
    name: 'Polylactic Acid (PLA)',
    terms: ['polylactic acid', 'pla', 'polylactide'],
    category: 'Microplastic polymer',
    severity: 'medium',
    explainer:
      'Bio-based polyester made from corn or sugarcane, marketed as compostable. Industrially compostable is an end-of-life claim, not a statement about particles: PLA is still a solid synthetic polymer and sheds particles in use.',
    useCases: ['"Compostable" glitter', 'Eco-marketed exfoliant beads', 'Compostable packaging and tea bags'],
    regulation: { eu: 'Biodegradable polymers can be exempt from the microplastics restriction only if they meet strict degradation testing; most PLA glitter does not.', us: 'No federal restriction.' },
    alternatives: ['Mica glitter', 'Jojoba esters for exfoliation', 'Salt or sugar scrubs'],
    queryHook: 'is pla a microplastic',
  },
  {
    slug: 'polyamide',
    name: 'Polyamide (and Polyamide-2 to -8)',
    terms: ['polyamide-1', 'polyamide-2', 'polyamide-3', 'polyamide-4', 'polyamide-5', 'polyamide-8', 'polyamide'],
    category: 'Microplastic polymer',
    severity: 'high',
    explainer:
      'The chemical family name for nylons. If a label says Polyamide rather than Nylon-12, it is the same class of solid synthetic polymer particle.',
    useCases: ['Powders and primers', 'Mascara fibres', 'Texture modifiers'],
    regulation: { eu: 'Within the EU microplastics restriction.', us: 'No federal restriction.' },
    alternatives: ['Rice starch', 'Silica', 'Cellulose fibres'],
    queryHook: 'is polyamide a microplastic',
  },
  {
    slug: 'polybutylene-succinate',
    name: 'Polybutylene Succinate (PBS)',
    terms: ['polybutylene succinate', 'pbs'],
    category: 'Microplastic polymer',
    severity: 'medium',
    explainer:
      'Biodegradable polyester positioned as a greener microbead replacement. Genuinely degrades faster than polyethylene under the right conditions, but it is still a solid synthetic polymer particle when you use it.',
    useCases: ['"Biodegradable" exfoliant beads', 'Eco-marketed scrubs', 'Compostable packaging'],
    regulation: { eu: 'Potentially exempt from the microplastics restriction if it passes biodegradation testing.', us: 'No federal restriction.' },
    alternatives: ['Jojoba esters', 'Ground apricot kernel', 'Oat or rice powder'],
    queryHook: 'is polybutylene succinate a microplastic',
  },
  {
    slug: 'polyethylene-isoterephthalate',
    name: 'Polyethylene Isoterephthalate',
    terms: ['polyethylene isoterephthalate', 'polyethylene terephthalate isoterephthalate'],
    category: 'Microplastic polymer',
    severity: 'high',
    explainer:
      'A PET variant used as glitter and film substrate in cosmetics. Chemically a close relative of the plastic used in drink bottles.',
    useCases: ['Cosmetic glitter', 'Holographic and shimmer particles', 'Nail art'],
    regulation: { eu: 'Restricted as a plastic glitter under the EU microplastics rules.', us: 'No federal restriction.' },
    alternatives: ['Mica', 'Synthetic fluorphlogopite', 'Mineral shimmer'],
    queryHook: 'is polyethylene isoterephthalate a microplastic',
  },
  {
    slug: 'polypropylene-terephthalate',
    name: 'Polypropylene Terephthalate',
    terms: ['polypropylene terephthalate'],
    category: 'Microplastic polymer',
    severity: 'high',
    explainer:
      'Polyester-family polymer used as a solid particle and film-former in long-wear cosmetics.',
    useCases: ['Long-wear complexion products', 'Glitter substrate', 'Film-formers'],
    regulation: { eu: 'Within the EU microplastics restriction.', us: 'No federal restriction.' },
    alternatives: ['Silica', 'Mica', 'Mineral fillers'],
    queryHook: 'is polypropylene terephthalate a microplastic',
  },
  {
    slug: 'ethylene-va-copolymer',
    name: 'Ethylene/VA Copolymer',
    terms: ['ethylene/va copolymer', 'ethylene vinyl acetate', 'eva'],
    category: 'Microplastic polymer',
    severity: 'medium',
    explainer:
      'EVA, the flexible plastic in trainer soles, used in cosmetics as a wax-like structurant and film-former. A synthetic polymer, though usually present as a soft matrix rather than a hard bead.',
    useCases: ['Lipstick and balm structure', 'Mascara film', 'Stick foundations', 'Hair waxes'],
    regulation: { eu: 'Permitted as a film-former; particle forms fall under the microplastics restriction.', us: 'No federal restriction.' },
    alternatives: ['Candelilla wax', 'Carnauba wax', 'Beeswax'],
    queryHook: 'is ethylene va copolymer a microplastic',
  },
  {
    slug: 'ethylene-acrylic-acid-copolymer',
    name: 'Ethylene/Acrylic Acid Copolymer',
    terms: ['ethylene/acrylic acid copolymer', 'ethylene acrylic acid'],
    category: 'Microplastic polymer',
    severity: 'medium',
    explainer:
      'Polyethylene-based copolymer used to thicken and suspend. Shares the polyethylene backbone that the EU microbead restriction targets.',
    useCases: ['Toothpaste', 'Thickened cleansers', 'Suspending glitter or beads in gels'],
    regulation: { eu: 'Within scope where present as solid particles.', us: 'No federal restriction.' },
    alternatives: ['Xanthan gum', 'Cellulose gum', 'Sclerotium gum'],
    queryHook: 'is ethylene acrylic acid copolymer a microplastic',
  },
  {
    slug: 'styrene-dvb-copolymer',
    name: 'Styrene/DVB Copolymer',
    terms: ['styrene/dvb copolymer', 'styrene divinylbenzene'],
    category: 'Microplastic polymer',
    severity: 'high',
    explainer:
      'Cross-linked polystyrene microspheres, used for a soft-focus optical effect. Solid, spherical, insoluble and cross-linked, which is close to the textbook definition of an intentionally added microplastic.',
    useCases: ['Soft-focus primers', 'Blurring creams', 'Mattifying products', 'Sunscreen texture'],
    regulation: { eu: 'Within the EU microplastics restriction.', us: 'No federal restriction.' },
    alternatives: ['Silica', 'Mica', 'Rice starch'],
    queryHook: 'is styrene dvb copolymer a microplastic',
  },
  {
    slug: 'polyimide-1',
    name: 'Polyimide-1',
    terms: ['polyimide-1', 'polyimide'],
    category: 'Microplastic polymer',
    severity: 'medium',
    explainer:
      'High-performance synthetic polymer used as a film-former in long-wear mascara and liner for smudge resistance.',
    useCases: ['Tubing mascara', 'Long-wear eyeliner', 'Brow gels'],
    regulation: { eu: 'Permitted as a film-former.', us: 'No federal restriction.' },
    alternatives: ['Shellac-based tubing mascara', 'Cellulose film-formers'],
    queryHook: 'is polyimide-1 a microplastic',
  },
  {
    slug: 'polyurethane-2',
    name: 'Polyurethane-2',
    terms: ['polyurethane-2'],
    category: 'Microplastic polymer',
    severity: 'medium',
    explainer:
      'Numbered polyurethane film-former, used where a flexible, water-resistant plastic film is wanted on skin or lashes.',
    useCases: ['Waterproof mascara', 'Long-wear foundation', 'Peel-off masks', 'Nail products'],
    regulation: { eu: 'Permitted as a film-former; particle forms restricted.', us: 'No federal restriction.' },
    alternatives: ['Shellac', 'Cellulose derivatives', 'Acacia gum'],
    queryHook: 'is polyurethane-2 a microplastic',
  },
  {
    slug: 'polyurethane-35',
    name: 'Polyurethane-35',
    terms: ['polyurethane-35'],
    category: 'Microplastic polymer',
    severity: 'medium',
    explainer:
      'Polyurethane film-former common in long-wear and transfer-resistant makeup. Same polymer class as Polyurethane-2, different chain specification.',
    useCases: ['Transfer-proof lip products', 'Long-wear foundation', 'Waterproof liner'],
    regulation: { eu: 'Permitted as a film-former.', us: 'No federal restriction.' },
    alternatives: ['Candelilla wax', 'Shellac', 'Plant-derived film-formers'],
    queryHook: 'is polyurethane-35 a microplastic',
  },
  {
    slug: 'polyethylene-polypropylene-copolymer',
    name: 'Polyethylene/Polypropylene Copolymer',
    terms: ['polyethylene/polypropylene copolymer', 'polyethylene polypropylene'],
    category: 'Microplastic polymer',
    severity: 'high',
    explainer:
      'Blend of the two most common commodity plastics, used as a wax-like texturiser. Both parent polymers are named targets of the EU microbead restriction.',
    useCases: ['Stick products', 'Thickened balms', 'Exfoliant particles', 'Texture modifiers'],
    regulation: { eu: 'Within the EU microplastics restriction where present as particles.', us: 'No federal restriction.' },
    alternatives: ['Carnauba wax', 'Candelilla wax', 'Rice bran wax'],
    queryHook: 'is polyethylene polypropylene copolymer a microplastic',
  },

  // ── Acrylate and vinyl polymers ───────────────────────────────────────────
  {
    slug: 'acrylates-c10-30-alkyl-acrylate-crosspolymer',
    name: 'Acrylates/C10-30 Alkyl Acrylate Crosspolymer',
    terms: ['acrylates/c10-30 alkyl acrylate crosspolymer', 'c10-30 alkyl acrylate'],
    category: 'Acrylate / vinyl polymer',
    severity: 'low',
    explainer:
      'Cross-linked acrylic thickener, extremely common in gels and lotions. A synthetic polymer, but it swells into a gel network rather than existing as a solid bead, which is why most regulators treat it differently from microbeads.',
    useCases: ['Gel moisturisers', 'Serums', 'Sunscreen texture', 'Cleansing gels'],
    regulation: { eu: 'Permitted; gel-forming polymers sit outside the solid-microplastic restriction.', us: 'No federal restriction.' },
    alternatives: ['Xanthan gum', 'Sclerotium gum', 'Cellulose gum'],
    queryHook: 'is acrylates c10-30 alkyl acrylate crosspolymer a microplastic',
  },
  {
    slug: 'sodium-polyacrylate',
    name: 'Sodium Polyacrylate',
    terms: ['sodium polyacrylate'],
    category: 'Acrylate / vinyl polymer',
    severity: 'low',
    explainer:
      'Superabsorbent acrylic polymer, the same material used in nappies. In cosmetics it thickens and holds water. Synthetic and persistent, but water-swellable rather than a solid particle.',
    useCases: ['Gel masks', 'Thickened serums', 'Hydrogel patches', 'Deodorant gels'],
    regulation: { eu: 'Permitted; not a solid microbead.', us: 'No federal restriction.' },
    alternatives: ['Xanthan gum', 'Hyaluronic acid', 'Agar'],
    queryHook: 'is sodium polyacrylate a microplastic',
  },
  {
    slug: 'sodium-acrylates-copolymer',
    name: 'Sodium Acrylates Copolymer',
    terms: ['sodium acrylates copolymer', 'sodium acrylate copolymer'],
    category: 'Acrylate / vinyl polymer',
    severity: 'low',
    explainer:
      'Acrylic thickener and stabiliser. Same family as sodium polyacrylate, used to build texture in water-based formulas.',
    useCases: ['Lotions and gels', 'Sunscreen', 'Hair styling gels'],
    regulation: { eu: 'Permitted.', us: 'No federal restriction.' },
    alternatives: ['Xanthan gum', 'Cellulose gum', 'Sclerotium gum'],
    queryHook: 'is sodium acrylates copolymer a microplastic',
  },
  {
    slug: 'polyacrylate-crosspolymer-6',
    name: 'Polyacrylate Crosspolymer-6',
    terms: ['polyacrylate crosspolymer-6', 'polyacrylate crosspolymer'],
    category: 'Acrylate / vinyl polymer',
    severity: 'low',
    explainer:
      'Cross-linked acrylic gelling agent prized for a light, non-tacky feel. Synthetic polymer, gel network rather than particle.',
    useCases: ['Light gel moisturisers', 'Primers', 'Serums', 'Sunscreen'],
    regulation: { eu: 'Permitted.', us: 'No federal restriction.' },
    alternatives: ['Xanthan gum', 'Sclerotium gum'],
    queryHook: 'is polyacrylate crosspolymer-6 a microplastic',
  },
  {
    slug: 'allyl-methacrylates-crosspolymer',
    name: 'Allyl Methacrylates Crosspolymer',
    terms: ['allyl methacrylates crosspolymer', 'allyl methacrylate'],
    category: 'Acrylate / vinyl polymer',
    severity: 'high',
    explainer:
      'Cross-linked methacrylate microspheres used for soft-focus and oil absorption. Solid, insoluble and cross-linked, so this is a genuine intentionally added microplastic rather than a gel.',
    useCases: ['Soft-focus primers', 'Mattifying powders', 'Blurring products'],
    regulation: { eu: 'Within the EU microplastics restriction.', us: 'No federal restriction.' },
    alternatives: ['Silica', 'Rice starch', 'Mica'],
    queryHook: 'is allyl methacrylates crosspolymer a microplastic',
  },
  {
    slug: 'acrylates-ammonium-methacrylate-copolymer',
    name: 'Acrylates/Ammonium Methacrylate Copolymer',
    terms: ['acrylates/ammonium methacrylate copolymer'],
    category: 'Acrylate / vinyl polymer',
    severity: 'medium',
    explainer:
      'Acrylic film-former used for water resistance and long wear. Deposits a thin plastic film rather than a particle.',
    useCases: ['Waterproof mascara', 'Long-wear foundation', 'Sunscreen water resistance'],
    regulation: { eu: 'Permitted as a film-former.', us: 'No federal restriction.' },
    alternatives: ['Shellac', 'Cellulose film-formers', 'Plant waxes'],
    queryHook: 'is acrylates ammonium methacrylate copolymer a microplastic',
  },
  {
    slug: 'acrylates-beheneth-25-methacrylate-copolymer',
    name: 'Acrylates/Beheneth-25 Methacrylate Copolymer',
    terms: ['acrylates/beheneth-25 methacrylate copolymer', 'beheneth-25 methacrylate'],
    category: 'Acrylate / vinyl polymer',
    severity: 'low',
    explainer:
      'Acrylic thickener that also carries a PEG-type chain, so it appears in both the acrylate and ethoxylate discussions. Functions as a rheology modifier.',
    useCases: ['Body washes', 'Thickened cleansers', 'Shampoo'],
    regulation: { eu: 'Permitted.', us: 'No federal restriction.' },
    alternatives: ['Xanthan gum', 'Coco-glucoside thickening systems'],
    queryHook: 'is acrylates beheneth-25 methacrylate copolymer a microplastic',
  },
  {
    slug: 'acrylates-steareth-20-methacrylate-crosspolymer',
    name: 'Acrylates/Steareth-20 Methacrylate Crosspolymer',
    terms: ['acrylates/steareth-20 methacrylate crosspolymer', 'steareth-20 methacrylate'],
    category: 'Acrylate / vinyl polymer',
    severity: 'low',
    explainer:
      'Cross-linked acrylic thickener with an ethoxylated chain. Builds viscosity in surfactant systems.',
    useCases: ['Shampoo and conditioner', 'Body wash', 'Liquid hand soap'],
    regulation: { eu: 'Permitted.', us: 'No federal restriction.' },
    alternatives: ['Xanthan gum', 'Guar derivatives'],
    queryHook: 'is acrylates steareth-20 methacrylate crosspolymer a microplastic',
  },
  {
    slug: 'ammonium-acryloyldimethyltaurate-vp-copolymer',
    name: 'Ammonium Acryloyldimethyltaurate/VP Copolymer',
    terms: ['ammonium acryloyldimethyltaurate/vp copolymer', 'acryloyldimethyltaurate'],
    category: 'Acrylate / vinyl polymer',
    severity: 'low',
    explainer:
      'Synthetic acrylic and vinyl copolymer used as a gelling and stabilising agent, popular in lightweight modern textures.',
    useCases: ['Serums', 'Gel-creams', 'Sunscreen', 'Essences'],
    regulation: { eu: 'Permitted.', us: 'No federal restriction.' },
    alternatives: ['Xanthan gum', 'Sclerotium gum', 'Hyaluronic acid'],
    queryHook: 'is ammonium acryloyldimethyltaurate vp copolymer a microplastic',
  },
  {
    slug: 'acrylamide-sodium-acryloyldimethyltaurate-copolymer',
    name: 'Acrylamide/Sodium Acryloyldimethyltaurate Copolymer',
    terms: ['acrylamide/sodium acryloyldimethyltaurate copolymer'],
    category: 'Acrylate / vinyl polymer',
    severity: 'medium',
    explainer:
      'Acrylamide-based thickener. The polymer itself is large and inert, but the acrylamide family attracts scrutiny because residual free acrylamide monomer is a concern in its own right.',
    useCases: ['Emulsion stabilisers', 'Lotions', 'Sunscreen'],
    regulation: { eu: 'Permitted with strict residual acrylamide monomer limits.', us: 'No federal restriction.' },
    alternatives: ['Xanthan gum', 'Cellulose gum'],
    queryHook: 'is acrylamide sodium acryloyldimethyltaurate copolymer safe',
  },
  {
    slug: 'vp-hexadecene-copolymer',
    name: 'VP/Hexadecene Copolymer',
    terms: ['vp/hexadecene copolymer', 'hexadecene copolymer'],
    category: 'Acrylate / vinyl polymer',
    severity: 'medium',
    explainer:
      'Vinylpyrrolidone copolymer used as a film-former and water-resistance aid, notably in sunscreen and long-wear colour.',
    useCases: ['Water-resistant sunscreen', 'Long-wear lip colour', 'Mascara'],
    regulation: { eu: 'Permitted as a film-former.', us: 'No federal restriction.' },
    alternatives: ['Plant waxes', 'Shellac', 'Mineral sunscreen with physical water resistance'],
    queryHook: 'is vp hexadecene copolymer a microplastic',
  },
  {
    slug: 'polyquaternium-7',
    name: 'Polyquaternium-7',
    terms: ['polyquaternium-7'],
    category: 'Acrylate / vinyl polymer',
    severity: 'low',
    explainer:
      'Cationic synthetic polymer used as a conditioning agent, one of the most common in shampoo. Water-soluble, so it is a synthetic polymer rather than a solid microplastic.',
    useCases: ['Shampoo and conditioner', 'Body wash', 'Styling products'],
    regulation: { eu: 'Permitted; water-soluble polymers sit outside the solid-microplastic restriction.', us: 'No federal restriction.' },
    alternatives: ['Guar hydroxypropyltrimonium chloride', 'Cetrimonium chloride', 'Behentrimonium methosulfate'],
    queryHook: 'is polyquaternium-7 a microplastic',
  },
  {
    slug: 'polyquaternium-10',
    name: 'Polyquaternium-10',
    terms: ['polyquaternium-10'],
    category: 'Acrylate / vinyl polymer',
    severity: 'low',
    explainer:
      'Cellulose-derived cationic conditioning polymer. Notable because its backbone is modified cellulose, making it partly plant-derived rather than fully petrochemical.',
    useCases: ['Shampoo', 'Conditioner', '2-in-1 products', 'Body wash'],
    regulation: { eu: 'Permitted; water-soluble.', us: 'No federal restriction.' },
    alternatives: ['Guar hydroxypropyltrimonium chloride', 'Behentrimonium methosulfate'],
    queryHook: 'is polyquaternium-10 a microplastic',
  },
  {
    slug: 'polyquaternium-11',
    name: 'Polyquaternium-11',
    terms: ['polyquaternium-11'],
    category: 'Acrylate / vinyl polymer',
    severity: 'low',
    explainer:
      'Vinylpyrrolidone-based cationic polymer used for hold and conditioning in styling products. Water-soluble synthetic polymer.',
    useCases: ['Hairspray and styling gel', 'Mousse', 'Setting lotions'],
    regulation: { eu: 'Permitted.', us: 'No federal restriction.' },
    alternatives: ['Acacia gum', 'Flaxseed gel', 'Guar derivatives'],
    queryHook: 'is polyquaternium-11 a microplastic',
  },
  {
    slug: 'butylene-ethylene-styrene-copolymer',
    name: 'Butylene/Ethylene/Styrene Copolymer',
    terms: ['butylene/ethylene/styrene copolymer'],
    category: 'Microplastic polymer',
    severity: 'medium',
    explainer:
      'Synthetic elastomer used to give lip and stick products flexible structure and shine. A styrene-based polymer, though present as a soft matrix rather than a bead.',
    useCases: ['Lip gloss and balm', 'Stick foundation', 'Cream products'],
    regulation: { eu: 'Permitted as a structurant.', us: 'No federal restriction.' },
    alternatives: ['Candelilla wax', 'Castor oil', 'Beeswax'],
    queryHook: 'is butylene ethylene styrene copolymer a microplastic',
  },

  // ── Silicones (siloxanes: synthetic polymers, NOT microplastics) ──────────
  {
    slug: 'cyclopentasiloxane',
    name: 'Cyclopentasiloxane (D5)',
    terms: ['cyclopentasiloxane', 'd5'],
    category: 'Silicone',
    severity: 'medium',
    explainer:
      'Volatile cyclic silicone, the D5 that EU regulators restricted. Not a microplastic, its backbone is silicon and oxygen rather than carbon, but it is restricted for persistence and bioaccumulation reasons.',
    useCases: ['Hair serums and heat protectants', 'Primers', 'Antiperspirant', 'Quick-dry products'],
    regulation: { eu: 'Restricted to 0.1% in wash-off cosmetics since 2020, with wider leave-on restrictions agreed.', us: 'No federal restriction.' },
    alternatives: ['Squalane', 'Coconut-derived esters', 'Jojoba oil'],
    queryHook: 'is cyclopentasiloxane a microplastic',
  },
  {
    slug: 'cyclotetrasiloxane',
    name: 'Cyclotetrasiloxane (D4)',
    terms: ['cyclotetrasiloxane', 'd4'],
    category: 'Silicone',
    severity: 'high',
    explainer:
      'Cyclic silicone with the strictest regulatory status of the group. Not a microplastic, but classified in the EU as a substance of very high concern for persistence, bioaccumulation and toxicity.',
    useCases: ['Legacy hair and skin formulas', 'Some antiperspirants'],
    regulation: { eu: 'Substance of Very High Concern; restricted to 0.1% in cosmetics.', us: 'No federal restriction.' },
    alternatives: ['Squalane', 'Plant oils', 'Silicone-free serums'],
    queryHook: 'is cyclotetrasiloxane a microplastic',
  },
  {
    slug: 'cyclohexasiloxane',
    name: 'Cyclohexasiloxane (D6)',
    terms: ['cyclohexasiloxane', 'd6'],
    category: 'Silicone',
    severity: 'medium',
    explainer:
      'Cyclic silicone grouped with D4 and D5 in EU restrictions. A siloxane rather than a carbon-chain plastic, so not a microplastic.',
    useCases: ['Hair care', 'Skin primers', 'Colour cosmetics'],
    regulation: { eu: 'Restricted alongside D4 and D5 for persistence concerns.', us: 'No federal restriction.' },
    alternatives: ['Squalane', 'Jojoba oil', 'Ester-based emollients'],
    queryHook: 'is cyclohexasiloxane a microplastic',
  },
  {
    slug: 'dimethiconol',
    name: 'Dimethiconol',
    terms: ['dimethiconol'],
    category: 'Silicone',
    severity: 'low',
    explainer:
      'Hydroxyl-terminated silicone used for shine and slip, especially in hair care. A siloxane, so outside the microplastic definition, though it is a persistent synthetic polymer.',
    useCases: ['Hair serums and shine sprays', 'Conditioners', 'Frizz control', 'Leave-in treatments'],
    regulation: { eu: 'Permitted; not among the restricted cyclic siloxanes.', us: 'No federal restriction.' },
    alternatives: ['Argan oil', 'Squalane', 'Broccoli seed oil'],
    queryHook: 'is dimethiconol a microplastic',
  },
  {
    slug: 'phenyl-trimethicone',
    name: 'Phenyl Trimethicone',
    terms: ['phenyl trimethicone'],
    category: 'Silicone',
    severity: 'low',
    explainer:
      'Silicone with a phenyl group that adds gloss and a higher refractive index. Siloxane backbone, so not a microplastic.',
    useCases: ['Hair shine products', 'Lip gloss', 'Foundation', 'Illuminating primers'],
    regulation: { eu: 'Permitted.', us: 'No federal restriction.' },
    alternatives: ['Castor oil for gloss', 'Squalane', 'Meadowfoam seed oil'],
    queryHook: 'is phenyl trimethicone a microplastic',
  },
  {
    slug: 'caprylyl-methicone',
    name: 'Caprylyl Methicone',
    terms: ['caprylyl methicone'],
    category: 'Silicone',
    severity: 'low',
    explainer:
      'Lightweight silicone often used as a replacement for the restricted cyclic siloxanes. Siloxane, not a microplastic.',
    useCases: ['Primers', 'Lightweight moisturisers', 'Sunscreen texture', 'Hair serums'],
    regulation: { eu: 'Permitted; a common D5 substitute.', us: 'No federal restriction.' },
    alternatives: ['Squalane', 'Coco-caprylate', 'Isoamyl laurate'],
    queryHook: 'is caprylyl methicone a microplastic',
  },
  {
    slug: 'cetearyl-methicone',
    name: 'Cetearyl Methicone',
    terms: ['cetearyl methicone'],
    category: 'Silicone',
    severity: 'low',
    explainer:
      'Wax-like silicone used to structure balms and sticks. A siloxane rather than a plastic.',
    useCases: ['Stick products', 'Balms', 'Water-resistant sunscreen', 'Creams'],
    regulation: { eu: 'Permitted.', us: 'No federal restriction.' },
    alternatives: ['Carnauba wax', 'Candelilla wax', 'Rice bran wax'],
    queryHook: 'is cetearyl methicone a microplastic',
  },
  {
    slug: 'stearyl-dimethicone',
    name: 'Stearyl Dimethicone',
    terms: ['stearyl dimethicone'],
    category: 'Silicone',
    severity: 'low',
    explainer:
      'Long-chain silicone wax used for structure and a silky finish. Siloxane, not a microplastic.',
    useCases: ['Stick foundations', 'Lip balm', 'Rich creams'],
    regulation: { eu: 'Permitted.', us: 'No federal restriction.' },
    alternatives: ['Beeswax', 'Candelilla wax', 'Shea butter'],
    queryHook: 'is stearyl dimethicone a microplastic',
  },
  {
    slug: 'methicone',
    name: 'Methicone',
    terms: ['methicone'],
    category: 'Silicone',
    severity: 'low',
    explainer:
      'Short-chain silicone frequently used to coat mineral pigments so they disperse evenly and resist water. Siloxane, not a microplastic.',
    useCases: ['Coating on mineral powders', 'Pressed powders', 'Mineral sunscreen'],
    regulation: { eu: 'Permitted.', us: 'No federal restriction.' },
    alternatives: ['Uncoated mineral pigments', 'Jojoba-coated pigments'],
    queryHook: 'is methicone a microplastic',
  },
  {
    slug: 'bis-peg-ppg-14-14-dimethicone',
    name: 'Bis-PEG/PPG-14/14 Dimethicone',
    terms: ['bis-peg/ppg-14/14 dimethicone', 'bis-peg/ppg dimethicone'],
    category: 'Silicone',
    severity: 'low',
    explainer:
      'Silicone modified with PEG and PPG chains to make it water-dispersible. Sits in both the silicone and ethoxylate conversations; not a microplastic.',
    useCases: ['Hair conditioners', 'Water-based serums', 'Cleansing balms'],
    regulation: { eu: 'Permitted; ethoxylated ingredients require 1,4-dioxane purity control.', us: 'No federal restriction.' },
    alternatives: ['Behentrimonium methosulfate', 'Plant-oil conditioning'],
    queryHook: 'is bis-peg ppg-14/14 dimethicone a microplastic',
  },
  {
    slug: 'silica-dimethyl-silylate',
    name: 'Silica Dimethyl Silylate',
    terms: ['silica dimethyl silylate', 'silica silylate'],
    category: 'Silicone',
    severity: 'low',
    explainer:
      'Silica particles surface-treated with a silicone. The core is mineral silica, the coating is a siloxane, so neither part is a microplastic.',
    useCases: ['Mattifying powders', 'Oil absorption', 'Primers', 'Dry shampoo'],
    regulation: { eu: 'Permitted.', us: 'No federal restriction.' },
    alternatives: ['Untreated silica', 'Rice starch', 'Kaolin clay'],
    queryHook: 'is silica dimethyl silylate a microplastic',
  },
  {
    slug: 'divinyldimethicone-dimethicone-copolymer',
    name: 'Divinyldimethicone/Dimethicone Copolymer',
    terms: ['divinyldimethicone/dimethicone copolymer'],
    category: 'Silicone',
    severity: 'low',
    explainer:
      'Silicone elastomer blend used for smoothing and frizz control. Siloxane chemistry, not a microplastic.',
    useCases: ['Smoothing hair treatments', 'Frizz serums', 'Skin primers'],
    regulation: { eu: 'Permitted.', us: 'No federal restriction.' },
    alternatives: ['Argan oil', 'Squalane', 'Shea butter'],
    queryHook: 'is divinyldimethicone dimethicone copolymer a microplastic',
  },
  {
    slug: 'polyphenylsilsesquioxane',
    name: 'Polyphenylsilsesquioxane',
    terms: ['polyphenylsilsesquioxane'],
    category: 'Silicone',
    severity: 'low',
    explainer:
      'Silicone resin used as a film-former for long wear and gloss. A siloxane resin, so outside the microplastic definition despite being a solid synthetic polymer.',
    useCases: ['Long-wear lip colour', 'Transfer-resistant foundation', 'Hair gloss'],
    regulation: { eu: 'Permitted.', us: 'No federal restriction.' },
    alternatives: ['Shellac', 'Plant waxes', 'Castor-oil gloss systems'],
    queryHook: 'is polyphenylsilsesquioxane a microplastic',
  },
  {
    slug: 'polysilicone-11',
    name: 'Polysilicone-11',
    terms: ['polysilicone-11'],
    category: 'Silicone',
    severity: 'low',
    explainer:
      'Cross-linked silicone elastomer that gives primers their characteristic velvet slip. Siloxane, not a microplastic.',
    useCases: ['Pore-filling primers', 'Blurring products', 'Long-wear foundation'],
    regulation: { eu: 'Permitted.', us: 'No federal restriction.' },
    alternatives: ['Silica-based primers', 'Rice starch', 'Squalane'],
    queryHook: 'is polysilicone-11 a microplastic',
  },
  {
    slug: 'hydrogenated-dimethicone',
    name: 'Hydrogenated Dimethicone',
    terms: ['hydrogenated dimethicone'],
    category: 'Silicone',
    severity: 'low',
    explainer:
      'Saturated silicone wax used for structure and water resistance. Siloxane backbone, not a microplastic.',
    useCases: ['Water-resistant sunscreen', 'Stick products', 'Balms'],
    regulation: { eu: 'Permitted.', us: 'No federal restriction.' },
    alternatives: ['Beeswax', 'Carnauba wax', 'Candelilla wax'],
    queryHook: 'is hydrogenated dimethicone a microplastic',
  },

  // ── Batch B, 2026-07-30: ethoxylates, plasticisers, PFAS, preservatives ───
  {
    slug: 'peg-100-stearate',
    name: 'PEG-100 Stearate',
    terms: ['peg-100 stearate'],
    category: 'PEG / glycol',
    severity: 'low',
    explainer:
      'Ethoxylated emulsifier, one of the most common in lotions. Not a microplastic. The real question with PEGs is manufacturing purity, since ethoxylation can leave traces of 1,4-dioxane, a probable human carcinogen.',
    useCases: ['Lotions and creams', 'Cleansers', 'Sunscreen', 'Hair conditioner'],
    regulation: { eu: 'Permitted; 1,4-dioxane must be removed to trace levels.', us: 'FDA monitors 1,4-dioxane; some US states cap it in cosmetics.' },
    alternatives: ['Glyceryl stearate', 'Cetearyl olivate', 'Sucrose stearate'],
    queryHook: 'is peg-100 stearate a microplastic',
  },
  {
    slug: 'peg-40-hydrogenated-castor-oil',
    name: 'PEG-40 Hydrogenated Castor Oil',
    terms: ['peg-40 hydrogenated castor oil'],
    category: 'PEG / glycol',
    severity: 'low',
    explainer:
      'Ethoxylated castor oil used to dissolve fragrance and oils into water. Plant-derived backbone, ethoxylated. Not a microplastic; the concern is 1,4-dioxane purity.',
    useCases: ['Micellar water', 'Fragrance solubiliser', 'Face mists', 'Clear gels'],
    regulation: { eu: 'Permitted with dioxane purity limits.', us: 'No federal restriction.' },
    alternatives: ['Polyglyceryl-4 caprate', 'Caprylyl/capryl glucoside', 'Solubiliser-free formulas'],
    queryHook: 'is peg-40 hydrogenated castor oil a microplastic',
  },
  {
    slug: 'peg-12-dimethicone',
    name: 'PEG-12 Dimethicone',
    terms: ['peg-12 dimethicone'],
    category: 'PEG / glycol',
    severity: 'low',
    explainer:
      'Water-dispersible silicone made by attaching PEG chains to a siloxane. Neither part is a microplastic, but it inherits the ethoxylate purity question.',
    useCases: ['Conditioners', 'Water-based serums', 'Cleansing products'],
    regulation: { eu: 'Permitted with dioxane purity limits.', us: 'No federal restriction.' },
    alternatives: ['Behentrimonium methosulfate', 'Plant-oil conditioning'],
    queryHook: 'is peg-12 dimethicone a microplastic',
  },
  {
    slug: 'peg-150-distearate',
    name: 'PEG-150 Distearate',
    terms: ['peg-150 distearate'],
    category: 'PEG / glycol',
    severity: 'low',
    explainer:
      'High-molecular-weight PEG used to thicken surfactant systems. Not a microplastic; a water-soluble ethoxylated polymer.',
    useCases: ['Shampoo', 'Body wash', 'Liquid soap'],
    regulation: { eu: 'Permitted with dioxane purity limits.', us: 'No federal restriction.' },
    alternatives: ['Salt thickening', 'Xanthan gum', 'Coco-glucoside systems'],
    queryHook: 'is peg-150 distearate a microplastic',
  },
  {
    slug: 'ppg-15-stearyl-ether',
    name: 'PPG-15 Stearyl Ether',
    terms: ['ppg-15 stearyl ether'],
    category: 'PEG / glycol',
    severity: 'low',
    explainer:
      'Propylene-glycol-based emollient giving a dry, silky finish. A synthetic polyether, not a microplastic.',
    useCases: ['Lightweight moisturisers', 'Hair products', 'Colour cosmetics'],
    regulation: { eu: 'Permitted.', us: 'No federal restriction.' },
    alternatives: ['Squalane', 'Coco-caprylate', 'Isoamyl laurate'],
    queryHook: 'is ppg-15 stearyl ether a microplastic',
  },
  {
    slug: 'dehp',
    name: 'DEHP (Diethylhexyl Phthalate)',
    terms: ['dehp', 'diethylhexyl phthalate', 'di(2-ethylhexyl) phthalate'],
    category: 'Plasticiser / chemical of concern',
    severity: 'high',
    explainer:
      'The most studied plasticiser and a recognised endocrine disruptor. Banned in EU cosmetics. It reaches food and bodies mainly through plastic packaging and processing equipment rather than through cosmetic formulas.',
    useCases: ['Historic use in fragrance and nail products', 'Still found via packaging migration and food processing'],
    regulation: { eu: 'Banned in cosmetics; SVHC and restricted in food-contact materials.', us: 'Banned in childrens toys; permitted in some food-contact uses.' },
    alternatives: ['Glass or steel food storage', 'Phthalate-free fragrance', 'Unfragranced products'],
    queryHook: 'what is dehp and is it in cosmetics',
  },
  {
    slug: 'dbp',
    name: 'DBP (Dibutyl Phthalate)',
    terms: ['dbp', 'dibutyl phthalate'],
    category: 'Plasticiser / chemical of concern',
    severity: 'high',
    explainer:
      'Phthalate plasticiser historically used to keep nail polish flexible. Banned in EU cosmetics and voluntarily removed by most major nail brands, but it persists in imported and unregulated products.',
    useCases: ['Legacy nail polish', 'Some imported nail products', 'Historic fragrance use'],
    regulation: { eu: 'Banned in cosmetics; SVHC.', us: 'Not federally banned in cosmetics; California requires disclosure.' },
    alternatives: ['"3-free" or "10-free" nail polish', 'Water-based polish'],
    queryHook: 'is dibutyl phthalate banned in cosmetics',
  },
  {
    slug: 'diethyl-phthalate',
    name: 'DEP (Diethyl Phthalate)',
    terms: ['diethyl phthalate', 'dep'],
    category: 'Plasticiser / chemical of concern',
    severity: 'medium',
    explainer:
      'The phthalate most likely to be hiding in a cosmetic today, because it is used as a fragrance fixative and can be declared simply as "fragrance" or "parfum" rather than by name.',
    useCases: ['Fragrance fixative', 'Perfume and body spray', 'Scented lotions', 'Hair products'],
    regulation: { eu: 'Permitted in cosmetics but must be declared if present above threshold; fragrance components can be grouped as parfum.', us: 'No federal restriction.' },
    alternatives: ['Fragrance-free products', 'Brands that publish full fragrance disclosure', 'Essential-oil scented products'],
    queryHook: 'is diethyl phthalate in fragrance',
  },
  {
    slug: 'bisphenol-a',
    name: 'Bisphenol A (BPA)',
    terms: ['bisphenol a', 'bpa'],
    category: 'Plasticiser / chemical of concern',
    severity: 'high',
    explainer:
      'Not a cosmetic ingredient. BPA matters because it is used to make polycarbonate and epoxy can linings, so exposure comes from packaging and food contact rather than a formula. Heat and age increase migration.',
    useCases: ['Epoxy can linings', 'Polycarbonate containers', 'Thermal receipt paper'],
    regulation: { eu: 'Banned in baby bottles and thermal paper; tightly restricted in food contact.', us: 'Banned in baby bottles and sippy cups; permitted in other food-contact uses.' },
    alternatives: ['Glass jars', 'Stainless steel', 'BPA-NI lined cans (an improvement, not an absence)'],
    queryHook: 'is bpa in cosmetics or packaging',
  },
  {
    slug: 'bisphenol-s',
    name: 'Bisphenol S (BPS)',
    terms: ['bisphenol s', 'bps'],
    category: 'Plasticiser / chemical of concern',
    severity: 'high',
    explainer:
      'The most common BPA replacement, and the reason "BPA-free" is a weaker claim than it sounds. BPS is structurally similar and shows comparable endocrine activity in studies, while being less regulated.',
    useCases: ['BPA-free can linings and receipts', 'BPA-free plastics'],
    regulation: { eu: 'Restricted in thermal paper; under review for wider action.', us: 'No federal restriction.' },
    alternatives: ['Glass and steel', 'Declining paper receipts', 'Oleoresin-lined cans'],
    queryHook: 'is bps safer than bpa',
  },
  {
    slug: 'triclosan',
    name: 'Triclosan',
    terms: ['triclosan'],
    category: 'Plasticiser / chemical of concern',
    severity: 'high',
    explainer:
      'Antibacterial agent, not a plastic. Included because it is a persistent endocrine-active chemical that appears alongside microplastics in the same personal-care products and the same wastewater.',
    useCases: ['Legacy antibacterial soap', 'Some toothpaste', 'Deodorant'],
    regulation: { eu: 'Restricted to specific product types at low concentration.', us: 'Banned from over-the-counter antiseptic washes since 2017; permitted in some toothpaste.' },
    alternatives: ['Plain soap and water', 'Zinc-based deodorant', 'Fluoride toothpaste without triclosan'],
    queryHook: 'is triclosan still in products',
  },
  {
    slug: 'butylphenyl-methylpropional',
    name: 'Butylphenyl Methylpropional (Lilial)',
    terms: ['butylphenyl methylpropional', 'lilial'],
    category: 'Plasticiser / chemical of concern',
    severity: 'high',
    explainer:
      'Synthetic floral fragrance ingredient banned in EU cosmetics from 2022 after classification as a reproductive toxicant. Not a plastic, but a good test of whether a brand keeps its formulas current.',
    useCases: ['Legacy fragranced products', 'Detergents and softeners', 'Imported cosmetics'],
    regulation: { eu: 'Banned in cosmetics since March 2022.', us: 'No federal restriction.' },
    alternatives: ['Fragrance-free products', 'EU-compliant reformulated versions'],
    queryHook: 'is lilial banned',
  },
  {
    slug: 'dmdm-hydantoin',
    name: 'DMDM Hydantoin',
    terms: ['dmdm hydantoin'],
    category: 'Plasticiser / chemical of concern',
    severity: 'medium',
    explainer:
      'Formaldehyde-releasing preservative. Not a plastic. Works by slowly releasing small amounts of formaldehyde, a known human carcinogen by inhalation, which is why it draws scrutiny in leave-on products.',
    useCases: ['Shampoo and conditioner', 'Lotions', 'Liquid soap'],
    regulation: { eu: 'Permitted with limits and mandatory "releases formaldehyde" labelling above threshold.', us: 'No federal restriction.' },
    alternatives: ['Phenoxyethanol-preserved formulas', 'Sodium benzoate and potassium sorbate', 'Airless packaging'],
    queryHook: 'is dmdm hydantoin safe',
  },
  {
    slug: 'imidazolidinyl-urea',
    name: 'Imidazolidinyl Urea',
    terms: ['imidazolidinyl urea'],
    category: 'Plasticiser / chemical of concern',
    severity: 'medium',
    explainer:
      'Formaldehyde-releasing preservative in the same family as DMDM hydantoin. Not a plastic.',
    useCases: ['Lotions and creams', 'Shampoo', 'Makeup'],
    regulation: { eu: 'Permitted with concentration limits and formaldehyde-release labelling.', us: 'No federal restriction.' },
    alternatives: ['Phenoxyethanol', 'Benzyl alcohol', 'Sodium benzoate systems'],
    queryHook: 'is imidazolidinyl urea a formaldehyde releaser',
  },
  {
    slug: 'diazolidinyl-urea',
    name: 'Diazolidinyl Urea',
    terms: ['diazolidinyl urea'],
    category: 'Plasticiser / chemical of concern',
    severity: 'medium',
    explainer:
      'Another formaldehyde-releasing preservative, generally releasing more formaldehyde than imidazolidinyl urea. Not a plastic.',
    useCases: ['Shampoo and body wash', 'Lotions', 'Wet wipes'],
    regulation: { eu: 'Permitted with limits and release labelling.', us: 'No federal restriction.' },
    alternatives: ['Phenoxyethanol', 'Potassium sorbate', 'Preservative systems without formaldehyde donors'],
    queryHook: 'is diazolidinyl urea safe',
  },
  {
    slug: 'quaternium-15',
    name: 'Quaternium-15',
    terms: ['quaternium-15'],
    category: 'Plasticiser / chemical of concern',
    severity: 'high',
    explainer:
      'Formaldehyde-releasing preservative and one of the more common contact allergens among preservatives. Banned in EU cosmetics. Not a plastic.',
    useCases: ['Legacy shampoos and lotions', 'Imported cosmetics'],
    regulation: { eu: 'Banned in cosmetics.', us: 'No federal restriction.' },
    alternatives: ['Phenoxyethanol', 'Sodium benzoate', 'Ethylhexylglycerin systems'],
    queryHook: 'is quaternium-15 banned',
  },
  {
    slug: 'perfluorodecalin',
    name: 'Perfluorodecalin',
    terms: ['perfluorodecalin'],
    category: 'Plasticiser / chemical of concern',
    severity: 'high',
    explainer:
      'A PFAS used in some skincare for an occlusive, oxygen-carrying effect. Not a microplastic, but a fluorinated compound from the class defined by extreme environmental persistence.',
    useCases: ['Anti-ageing serums', 'Some "oxygenating" skincare', 'Post-procedure creams'],
    regulation: { eu: 'Permitted, but within scope of the proposed universal PFAS restriction.', us: 'Restricted in cosmetics by several US states.' },
    alternatives: ['Squalane', 'Ceramide-based occlusives', 'Shea butter'],
    queryHook: 'is perfluorodecalin a pfas',
  },
  {
    slug: 'c9-15-fluoroalcohol-phosphate',
    name: 'C9-15 Fluoroalcohol Phosphate',
    terms: ['c9-15 fluoroalcohol phosphate'],
    category: 'Plasticiser / chemical of concern',
    severity: 'high',
    explainer:
      'PFAS used for water and oil repellency in cosmetics. A fluorinated surfactant rather than a microplastic, and among the ingredients state-level PFAS cosmetic bans specifically target.',
    useCases: ['Long-wear foundation', 'Waterproof makeup', 'Powder coatings'],
    regulation: { eu: 'Within scope of the proposed universal PFAS restriction.', us: 'Restricted by state PFAS-in-cosmetics laws.' },
    alternatives: ['Wax-based water resistance', 'Mineral makeup', 'Silicone-based long wear'],
    queryHook: 'is c9-15 fluoroalcohol phosphate a pfas',
  },
  {
    slug: 'polyperfluoromethylisopropyl-ether',
    name: 'Polyperfluoromethylisopropyl Ether',
    terms: ['polyperfluoromethylisopropyl ether'],
    category: 'Plasticiser / chemical of concern',
    severity: 'high',
    explainer:
      'Fluoropolymer ether used as a conditioning and water-repellent agent. Both a synthetic polymer and a PFAS, which makes it one of the least desirable entries on any ingredient list.',
    useCases: ['Long-wear cosmetics', 'Hair treatments', 'Water-repellent formulas'],
    regulation: { eu: 'Within scope of the proposed universal PFAS restriction.', us: 'Restricted by state PFAS-in-cosmetics laws.' },
    alternatives: ['Plant waxes', 'Squalane', 'Mineral-based alternatives'],
    queryHook: 'is polyperfluoromethylisopropyl ether a pfas',
  },
  {
    slug: 'resorcinol',
    name: 'Resorcinol',
    terms: ['resorcinol'],
    category: 'Plasticiser / chemical of concern',
    severity: 'medium',
    explainer:
      'Hair-dye and acne ingredient with thyroid and sensitisation concerns. Not a plastic, included because it co-occurs with the plastics discussion in "clean beauty" screening.',
    useCases: ['Permanent hair dye', 'Some acne treatments'],
    regulation: { eu: 'Permitted in hair dye at restricted concentrations with warning labelling.', us: 'No federal restriction.' },
    alternatives: ['Henna-based colour', 'Resorcinol-free dye lines', 'Salicylic acid for acne'],
    queryHook: 'is resorcinol safe in hair dye',
  },
  {
    slug: 'toluene',
    name: 'Toluene',
    terms: ['toluene'],
    category: 'Plasticiser / chemical of concern',
    severity: 'high',
    explainer:
      'Solvent historically used in nail polish, and one of the original "toxic trio" alongside formaldehyde and DBP. A neurotoxicant and reproductive toxicant. Not a plastic.',
    useCases: ['Legacy nail polish', 'Nail treatments', 'Some imported products'],
    regulation: { eu: 'Restricted in cosmetics; banned above trace levels in nail products.', us: 'Not federally banned; California requires disclosure.' },
    alternatives: ['"3-free" or higher nail polish', 'Water-based polish', 'Ethyl acetate systems'],
    queryHook: 'is toluene still in nail polish',
  },
]

// Order severities for sorting / display across all consumers.
export const SEVERITY_ORDER: Record<IngredientSeverity, number> = { high: 0, medium: 1, low: 2, none: 3 }

export function getIngredientBySlug(slug: string): IngredientFlag | undefined {
  // Resolves against flagged AND cleared entries. ALL_INGREDIENT_PAGES is
  // declared below; that is safe because this runs at call time, after module
  // initialisation, not during it.
  return ALL_INGREDIENT_PAGES.find((i) => i.slug === slug)
}

export function getRelatedIngredients(slug: string, limit = 4): IngredientFlag[] {
  const target = getIngredientBySlug(slug)
  if (!target) return INGREDIENTS.slice(0, limit)
  // Neighbours within the category, not its first four: taking the first four
  // left half the ingredient pages with no link but the hub.
  const sameCategory = ALL_INGREDIENT_PAGES.filter((i) => i.category === target.category)
  const index = sameCategory.findIndex((i) => i.slug === slug)
  return neighbours(sameCategory, index, limit, { wrap: true })
}


// ─────────────────────────────────────────────────────────────────────────────
// CLEARED INGREDIENTS — deliberately a SEPARATE array.
//
// These are ingredients people search "is X a microplastic" for where the
// honest answer is NO. They exist because that query has real volume and
// because being the source that says "this one is fine" is worth more to trust
// than flagging everything.
//
// They must never be merged into INGREDIENTS. IngredientChecker.tsx builds its
// FLAGS list from INGREDIENTS and treats every term match as a hazard, so
// adding these there would make the checker flag silica, glycerin and cetearyl
// alcohol as problems — actively wrong, and exactly the fear-mongering that
// gets ingredient apps criticised. Page generation and the sitemap use
// ALL_INGREDIENT_PAGES below; the checker keeps using INGREDIENTS alone.
// ─────────────────────────────────────────────────────────────────────────────
export const CLEARED_INGREDIENTS: IngredientFlag[] = [
  {
    slug: 'silica',
    name: 'Silica',
    terms: ['silica'],
    category: 'Cleared / not a microplastic',
    severity: 'none',
    explainer:
      'Not a microplastic. Silica is silicon dioxide, a mineral, chemically the same family as sand and glass. It is one of the main materials brands use to REPLACE plastic microbeads for oil absorption and soft focus.',
    useCases: ['Mattifying powders', 'Oil absorption', 'Primers', 'Toothpaste (as a mild abrasive)'],
    regulation: { eu: 'Permitted; outside the microplastics restriction entirely as a mineral.', us: 'Permitted.' },
    alternatives: ['No swap needed — silica is often the swap'],
    queryHook: 'is silica a microplastic',
  },
  {
    slug: 'mica',
    name: 'Mica',
    terms: ['mica'],
    category: 'Cleared / not a microplastic',
    severity: 'none',
    explainer:
      'Not a microplastic. Mica is a naturally occurring mineral and the standard non-plastic alternative to polyester glitter. Its genuine issue is ethical rather than chemical: mica mining has documented child-labour problems, so look for responsibly sourced or synthetic fluorphlogopite.',
    useCases: ['Shimmer and highlighter', 'Eyeshadow', 'Non-plastic glitter', 'Pressed powders'],
    regulation: { eu: 'Permitted; a mineral, outside the microplastics restriction.', us: 'Permitted.' },
    alternatives: ['Synthetic fluorphlogopite (lab-grown mica) for ethical sourcing'],
    queryHook: 'is mica a microplastic',
  },
  {
    slug: 'cellulose',
    name: 'Cellulose',
    terms: ['cellulose', 'microcrystalline cellulose'],
    category: 'Cleared / not a microplastic',
    severity: 'none',
    explainer:
      'Not a microplastic. Cellulose is a plant polymer — the structural material of wood and cotton. It is a polymer, but not a synthetic one, and it biodegrades. Widely used as a plastic-bead replacement.',
    useCases: ['Exfoliant particles', 'Mascara fibres', 'Thickener', 'Loose powders'],
    regulation: { eu: 'Permitted; natural polymers are explicitly exempt from the microplastics restriction.', us: 'Permitted.' },
    alternatives: ['No swap needed'],
    queryHook: 'is cellulose a microplastic',
  },
  {
    slug: 'jojoba-esters',
    name: 'Jojoba Esters (Jojoba Beads)',
    terms: ['jojoba esters', 'jojoba beads', 'hydrogenated jojoba oil'],
    category: 'Cleared / not a microplastic',
    severity: 'none',
    explainer:
      'Not a microplastic, and the single best-known replacement for polyethylene microbeads. Naturally derived wax esters formed into soft spheres that exfoliate and then melt on the skin rather than washing into waterways.',
    useCases: ['Facial scrubs', 'Body exfoliants', 'Cleansing balms'],
    regulation: { eu: 'Permitted; naturally derived and biodegradable.', us: 'Permitted.' },
    alternatives: ['No swap needed — this is the swap'],
    queryHook: 'are jojoba beads plastic',
  },
  {
    slug: 'cetearyl-alcohol',
    name: 'Cetearyl Alcohol',
    terms: ['cetearyl alcohol', 'cetyl alcohol', 'stearyl alcohol'],
    category: 'Cleared / not a microplastic',
    severity: 'none',
    explainer:
      'Not a microplastic and not a drying alcohol. This is one of the most commonly misunderstood ingredients on a label: it is a fatty alcohol, usually from coconut or palm, used to thicken and soften. It behaves like a wax, not like ethanol.',
    useCases: ['Conditioners', 'Creams and lotions', 'Emulsion stabiliser'],
    regulation: { eu: 'Permitted.', us: 'Permitted.' },
    alternatives: ['No swap needed'],
    queryHook: 'is cetearyl alcohol bad for you',
  },
  {
    slug: 'xanthan-gum',
    name: 'Xanthan Gum',
    terms: ['xanthan gum'],
    category: 'Cleared / not a microplastic',
    severity: 'none',
    explainer:
      'Not a microplastic. A polysaccharide produced by bacterial fermentation of sugar, used to thicken. It is the standard natural substitute for synthetic acrylate thickeners like carbomer.',
    useCases: ['Gels and serums', 'Natural thickener', 'Emulsion stabiliser'],
    regulation: { eu: 'Permitted; a biopolymer, exempt from the microplastics restriction.', us: 'Permitted.' },
    alternatives: ['No swap needed — often the swap for carbomer'],
    queryHook: 'is xanthan gum a microplastic',
  },
  {
    slug: 'hyaluronic-acid',
    name: 'Hyaluronic Acid',
    terms: ['hyaluronic acid', 'sodium hyaluronate'],
    category: 'Cleared / not a microplastic',
    severity: 'none',
    explainer:
      'Not a microplastic. A polysaccharide your own body produces, made commercially by bacterial fermentation. It is a polymer in the technical sense and entirely biological.',
    useCases: ['Hydrating serums', 'Moisturisers', 'Sheet masks', 'Eye creams'],
    regulation: { eu: 'Permitted; biopolymer.', us: 'Permitted.' },
    alternatives: ['No swap needed'],
    queryHook: 'is hyaluronic acid a microplastic',
  },
  {
    slug: 'shellac',
    name: 'Shellac',
    terms: ['shellac'],
    category: 'Cleared / not a microplastic',
    severity: 'none',
    explainer:
      'Not a microplastic. A natural resin secreted by the lac insect, used as a film-former. It is the main biological alternative to synthetic film-formers in tubing mascara and long-wear products. Not vegan.',
    useCases: ['Tubing mascara', 'Hair products', 'Nail products', 'Natural film-former'],
    regulation: { eu: 'Permitted; natural resin.', us: 'Permitted.' },
    alternatives: ['Candelilla wax for a vegan film-former'],
    queryHook: 'is shellac a plastic',
  },
  {
    slug: 'zinc-oxide',
    name: 'Zinc Oxide',
    terms: ['zinc oxide'],
    category: 'Cleared / not a microplastic',
    severity: 'none',
    explainer:
      'Not a microplastic. A mineral UV filter and the active in mineral sunscreen. Choosing a mineral sunscreen is a straightforward way to avoid the plastic film-formers that chemical sunscreens often rely on for water resistance.',
    useCases: ['Mineral sunscreen', 'Nappy cream', 'Calamine products', 'Mineral makeup'],
    regulation: { eu: 'Approved UV filter; nano forms require labelling.', us: 'FDA-approved sunscreen active, generally recognised as safe and effective.' },
    alternatives: ['No swap needed'],
    queryHook: 'is zinc oxide a microplastic',
  },
  {
    slug: 'titanium-dioxide',
    name: 'Titanium Dioxide',
    terms: ['titanium dioxide'],
    category: 'Cleared / not a microplastic',
    severity: 'none',
    explainer:
      'Not a microplastic. A mineral used as a UV filter and white pigment. Worth one distinction: the EU banned it as a FOOD additive (E171) over genotoxicity uncertainty, while topical cosmetic and sunscreen use remains approved.',
    useCases: ['Mineral sunscreen', 'White pigment in makeup', 'Toothpaste'],
    regulation: { eu: 'Approved in cosmetics; banned as food additive E171 since 2022; nano forms require labelling.', us: 'FDA-approved sunscreen active; permitted in food.' },
    alternatives: ['Zinc oxide for sunscreen if preferred'],
    queryHook: 'is titanium dioxide a microplastic',
  },
  {
    slug: 'rice-starch',
    name: 'Rice Starch',
    terms: ['rice starch', 'oryza sativa starch', 'tapioca starch'],
    category: 'Cleared / not a microplastic',
    severity: 'none',
    explainer:
      'Not a microplastic. A plant starch used for oil absorption and a soft-focus finish, and one of the main replacements for Nylon-12 and PMMA microspheres in clean-formulated powders.',
    useCases: ['Loose and pressed powders', 'Dry shampoo', 'Mattifying primers', 'Baby powder'],
    regulation: { eu: 'Permitted; natural polymer, exempt.', us: 'Permitted.' },
    alternatives: ['No swap needed — this is the swap for nylon powders'],
    queryHook: 'is rice starch a microplastic',
  },
  {
    slug: 'bamboo-powder',
    name: 'Bamboo Powder',
    terms: ['bamboo powder', 'bambusa vulgaris', 'bamboo stem powder'],
    category: 'Cleared / not a microplastic',
    severity: 'none',
    explainer:
      'Not a microplastic. Ground bamboo used as a physical exfoliant and oil absorber. A plant material, biodegradable, and a common microbead replacement. Can be sharp-edged, so it is harsher on skin than jojoba beads.',
    useCases: ['Facial and body scrubs', 'Powder cleansers', 'Oil absorption'],
    regulation: { eu: 'Permitted; natural material.', us: 'Permitted.' },
    alternatives: ['Jojoba esters if you want a gentler exfoliant'],
    queryHook: 'is bamboo powder a microplastic',
  },
  {
    slug: 'carnauba-wax',
    name: 'Carnauba Wax',
    terms: ['carnauba wax', 'copernicia cerifera'],
    category: 'Cleared / not a microplastic',
    severity: 'none',
    explainer:
      'Not a microplastic. A plant wax from palm leaves, the hardest natural wax available, and the main structural alternative to synthetic polymer waxes in sticks and balms.',
    useCases: ['Lipstick and balm structure', 'Mascara', 'Stick foundations', 'Nail products'],
    regulation: { eu: 'Permitted; natural wax.', us: 'Permitted.' },
    alternatives: ['No swap needed'],
    queryHook: 'is carnauba wax a plastic',
  },
  {
    slug: 'candelilla-wax',
    name: 'Candelilla Wax',
    terms: ['candelilla wax', 'euphorbia cerifera'],
    category: 'Cleared / not a microplastic',
    severity: 'none',
    explainer:
      'Not a microplastic. A plant wax from the candelilla shrub and the standard vegan alternative to beeswax. Used to structure sticks and balms in place of synthetic polymers.',
    useCases: ['Vegan lip balm and lipstick', 'Stick products', 'Mascara', 'Cream structure'],
    regulation: { eu: 'Permitted; natural wax.', us: 'Permitted.' },
    alternatives: ['No swap needed'],
    queryHook: 'is candelilla wax a plastic',
  },
  {
    slug: 'beeswax',
    name: 'Beeswax',
    terms: ['beeswax', 'cera alba'],
    category: 'Cleared / not a microplastic',
    severity: 'none',
    explainer:
      'Not a microplastic. A natural wax that structures balms and creams. Not vegan, and worth noting that beeswax food wraps are a genuinely good replacement for cling film.',
    useCases: ['Lip balm', 'Salves and ointments', 'Cream structure', 'Food wraps'],
    regulation: { eu: 'Permitted; natural wax.', us: 'Permitted.' },
    alternatives: ['Candelilla wax for a vegan option'],
    queryHook: 'is beeswax a plastic',
  },
  {
    slug: 'squalane',
    name: 'Squalane',
    terms: ['squalane'],
    category: 'Cleared / not a microplastic',
    severity: 'none',
    explainer:
      'Not a microplastic. A lightweight emollient, now usually derived from sugarcane or olives. It is the most common plant-based replacement for silicones and synthetic emollients, with a similar silky finish.',
    useCases: ['Facial oils and serums', 'Moisturisers', 'Silicone-free primers', 'Hair oils'],
    regulation: { eu: 'Permitted.', us: 'Permitted.' },
    alternatives: ['No swap needed — often the swap for dimethicone'],
    queryHook: 'is squalane a silicone',
  },
  {
    slug: 'glycerin',
    name: 'Glycerin',
    terms: ['glycerin', 'glycerine', 'glycerol'],
    category: 'Cleared / not a microplastic',
    severity: 'none',
    explainer:
      'Not a microplastic and not a polymer at all. A simple, small humectant molecule that draws water into skin, and among the best-studied and safest ingredients in cosmetics.',
    useCases: ['Nearly all moisturisers', 'Cleansers', 'Toothpaste', 'Serums'],
    regulation: { eu: 'Permitted.', us: 'Permitted.' },
    alternatives: ['No swap needed'],
    queryHook: 'is glycerin a microplastic',
  },
  {
    slug: 'chitosan',
    name: 'Chitosan',
    terms: ['chitosan'],
    category: 'Cleared / not a microplastic',
    severity: 'none',
    explainer:
      'Not a microplastic. A biopolymer made from chitin in crustacean shells or fungi, used as a film-former and thickener. Biodegradable, and studied as a natural flocculant for removing microplastics from water.',
    useCases: ['Film-formers', 'Hair fixatives', 'Wound care', 'Natural thickener'],
    regulation: { eu: 'Permitted; biopolymer.', us: 'Permitted.' },
    alternatives: ['No swap needed'],
    queryHook: 'is chitosan a microplastic',
  },
  {
    slug: 'carrageenan',
    name: 'Carrageenan',
    terms: ['carrageenan'],
    category: 'Cleared / not a microplastic',
    severity: 'none',
    explainer:
      'Not a microplastic. A polysaccharide extracted from red seaweed, used to thicken and gel. Some digestive-inflammation debate exists around the degraded form in food, but it is not a plastic in any sense.',
    useCases: ['Toothpaste', 'Natural gels', 'Plant milks (as a food thickener)'],
    regulation: { eu: 'Permitted in cosmetics and food.', us: 'Permitted; some debate over degraded forms.' },
    alternatives: ['Xanthan gum if avoiding carrageenan in food'],
    queryHook: 'is carrageenan a microplastic',
  },
  {
    slug: 'stearic-acid',
    name: 'Stearic Acid',
    terms: ['stearic acid'],
    category: 'Cleared / not a microplastic',
    severity: 'none',
    explainer:
      'Not a microplastic. A fatty acid from plant or animal fats, used to thicken and stabilise. Like cetearyl alcohol, it is frequently mistaken for something synthetic because of the chemical-sounding name.',
    useCases: ['Creams and lotions', 'Soap', 'Stick products', 'Emulsion stabiliser'],
    regulation: { eu: 'Permitted.', us: 'Permitted.' },
    alternatives: ['No swap needed'],
    queryHook: 'is stearic acid a microplastic',
  },
]

/**
 * Every ingredient that gets its own page: flagged plus cleared.
 * Used by the index page, [slug] page generation, and the sitemap.
 * NOT used by IngredientChecker, which must only see INGREDIENTS.
 */
export const ALL_INGREDIENT_PAGES: IngredientFlag[] = [...INGREDIENTS, ...CLEARED_INGREDIENTS]
